import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { LuX } from "react-icons/lu";
import { colors, effects } from "../../design/tokens";

interface ModalLayoutProps {
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
}

export interface ModalLayoutRef {
  triggerClose: () => void;
}

export const ModalLayout = forwardRef<ModalLayoutRef, ModalLayoutProps>(
  ({ onClose, children, className = "" }, ref) => {
    const [isClosing, setIsClosing] = useState(false);

    useImperativeHandle(ref, () => ({
      triggerClose: () => {
        if (!isClosing) {
          setIsClosing(true);
          setTimeout(() => {
            onClose();
          }, 300);
        }
      },
    }));

    const handleClose = () => {
      if (!isClosing) {
        setIsClosing(true);
        setTimeout(() => {
          onClose();
        }, 300);
      }
    };

    // Handle ESC key to close modal
    useEffect(() => {
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          handleClose();
        }
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    return (
      <div
        className={`${
          isClosing ? "animate-fade-out" : "animate-fade-in"
        } absolute inset-0 p-8
        flex items-center justify-center
        bg-black/25 backdrop-blur-sm
        ${effects.transition.default} z-50`}
        onClick={handleClose}
      >
        <div
          className={`max-h-[600px]
            ${effects.borderRadius.lg} relative
          bg-dark-surface-primary
          shadow-[0_4px_2.6px_0_rgba(0,0,0,0.46),0_3px_14.2px_0_rgba(0,0,0,0.3)]
          transform ${effects.transition.default}
          overflow-hidden ${className}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={handleClose}
            className={`absolute top-3 right-3 z-10
            ${colors.text.secondary}
            ${colors.hover.brightness} ${effects.transition.default}`}
          >
            <LuX size={24} />
          </button>

          <div className="w-full h-full overflow-y-auto">{children}</div>
        </div>
      </div>
    );
  }
);
